import { LLMProvider } from './base';

export class OpenAIProvider extends LLMProvider {
  constructor(apiKey, config = {}) {
    super(apiKey, config);
    this.model = config.model || 'gpt-4o';
    this.baseURL = config.baseURL || 'https://api.openai.com/v1';
  }

  async chat(messages, systemPrompt = '') {
    return this.retryWithBackoff(async () => {
      const messagesArray = systemPrompt
        ? [{ role: 'system', content: systemPrompt }, { role: 'user', content: messages }]
        : [{ role: 'user', content: messages }];

      const response = await fetch(`${this.baseURL}/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: this.model,
          messages: messagesArray,
          max_tokens: this.config.maxTokens || 2000,
          temperature: this.config.temperature || 0.7
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'OpenAI API error');
      }

      const data = await response.json();
      return data.choices[0].message.content;
    });
  }

  async chatMultiTurn(messages, systemPrompt = '') {
    return this.retryWithBackoff(async () => {
      const messagesArray = systemPrompt
        ? [{ role: 'system', content: systemPrompt }, ...messages]
        : [...messages];

      const response = await fetch(`${this.baseURL}/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: this.model,
          messages: messagesArray,
          max_tokens: this.config.maxTokens || 4096,
          temperature: this.config.temperature || 0.7
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'OpenAI API error');
      }

      const data = await response.json();
      return data.choices[0].message.content;
    });
  }

  async chatMultiTurnStream(messages, systemPrompt = '', onChunk = () => {}) {
    const messagesArray = systemPrompt
      ? [{ role: 'system', content: systemPrompt }, ...messages]
      : [...messages];

    const response = await fetch(`${this.baseURL}/chat/completions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.apiKey}`
      },
      body: JSON.stringify({
        model: this.model,
        messages: messagesArray,
        max_tokens: this.config.maxTokens || 4096,
        temperature: this.config.temperature || 0.7,
        stream: true
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'OpenAI API error');
    }

    let fullText = '';
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed.startsWith('data: ')) continue;
        const data = trimmed.slice(6);
        if (data === '[DONE]') break;

        try {
          const parsed = JSON.parse(data);
          const delta = parsed.choices?.[0]?.delta?.content;
          if (delta) {
            fullText += delta;
            onChunk(delta);
          }
        } catch {
          // skip malformed SSE lines
        }
      }
    }

    return fullText;
  }

  async chatWithImage(images, textPrompt, systemPrompt = '') {
    return this.retryWithBackoff(async () => {
      const imageBlocks = images.map(img => ({
        type: 'image_url',
        image_url: { url: `data:${img.mimeType};base64,${img.base64}` }
      }));
      const messages = [
        ...(systemPrompt ? [{ role: 'system', content: systemPrompt }] : []),
        { role: 'user', content: [...imageBlocks, { type: 'text', text: textPrompt }] }
      ];
      const response = await fetch(`${this.baseURL}/chat/completions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${this.apiKey}` },
        body: JSON.stringify({ model: this.model, messages, max_tokens: 4096 })
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'OpenAI API error');
      }
      const data = await response.json();
      return data.choices[0].message.content;
    });
  }

  validateApiKey() {
    return this.apiKey && this.apiKey.startsWith('sk-');
  }

  getModelName() {
    return 'GPT-4 (OpenAI)';
  }
}
